<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-50 overflow-y-auto"
        @click.self="$emit('close')"
      >
        <!-- Backdrop -->
        <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>

        <!-- Modal -->
        <div class="flex min-h-full items-center justify-center p-4">
          <div class="relative bg-white border border-gray-200 dark:border-gray-700  dark:bg-black rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <!-- Header -->
            <div class="sticky top-0 bg-white dark:bg-black border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
              <div class="flex items-center">
                <div 
                  class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center mr-3"
                  :style="{ backgroundColor: brandColors.primary + '20' }"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5" :style="{ color: brandColors.primary }">
                    <path d="M21.731 2.269a2.625 2.625 0 0 0-3.712 0l-1.157 1.157 3.712 3.712 1.157-1.157a2.625 2.625 0 0 0 0-3.712ZM19.513 8.199l-3.712-3.712-8.4 8.4a5.25 5.25 0 0 0-1.32 2.214l-.8 2.685a.75.75 0 0 0 .933.933l2.685-.8a5.25 5.25 0 0 0 2.214-1.32l8.4-8.4Z" />
                    <path d="M5.25 5.25a3 3 0 0 0-3 3v10.5a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3V13.5a.75.75 0 0 0-1.5 0v5.25a1.5 1.5 0 0 1-1.5 1.5H5.25a1.5 1.5 0 0 1-1.5-1.5V8.25a1.5 1.5 0 0 1 1.5-1.5h5.25a.75.75 0 0 0 0-1.5H5.25Z" />
                  </svg>
                </div>
                <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
                  Edit Profile
                </h3>
              </div>
              <button
                @click="$emit('close')"
                class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors duration-200"
              >
                <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <!-- Form -->
            <form @submit.prevent="handleSubmit" class="px-6 py-4">
              <!-- Error Message -->
              <div v-if="error || validationError" class="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <div class="flex items-start">
                  <svg class="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                  </svg>
                  <p class="text-sm text-red-600 dark:text-red-400">{{ validationError || error }}</p>
                </div>
              </div>

              <!-- Personal Information -->
              <div class="mb-6">
                <h4 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Personal Information</h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label for="first_name" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      First Name <span class="text-red-500">*</span>
                    </label>
                    <input
                      id="first_name"
                      v-model="formData.first_name"
                      type="text"
                      required
                      class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:border-transparent bg-white dark:bg-black text-gray-900 dark:text-white transition-colors"
                      :style="{ 
                        borderColor: brandColors.primary,
                        '--tw-ring-color': brandColors.primary,
                        'outline': 'none'
                      }"
                    />
                  </div>
                  <div>
                    <label for="last_name" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Last Name <span class="text-red-500">*</span>
                    </label>
                    <input
                      id="last_name"
                      v-model="formData.last_name"
                      type="text"
                      required
                      class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:border-transparent bg-white dark:bg-black text-gray-900 dark:text-white transition-colors"
                      :style="{ 
                        borderColor: brandColors.primary,
                        '--tw-ring-color': brandColors.primary,
                        'outline': 'none'
                      }"
                    />
                  </div>
                  <div>
                    <label for="email" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email <span class="text-red-500">*</span>
                    </label>
                    <input
                      id="email"
                      v-model="formData.email"
                      type="email"
                      required
                      class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:border-transparent bg-white dark:bg-black text-gray-900 dark:text-white transition-colors"
                      :style="{ 
                        borderColor: brandColors.primary,
                        '--tw-ring-color': brandColors.primary,
                        'outline': 'none'
                      }"
                    />
                  </div>
                  <div>
                    <label for="mobile" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Mobile Number <span class="text-red-500">*</span>
                    </label>
                    <input
                      id="mobile"
                      v-model="formData.mobile"
                      type="text"
                      required
                      @input="formData.mobile = formData.mobile.replace(/[^0-9]/g, '')"
                      class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:border-transparent bg-white dark:bg-black text-gray-900 dark:text-white transition-colors"
                      :style="{ 
                        borderColor: brandColors.primary,
                        '--tw-ring-color': brandColors.primary,
                        'outline': 'none'
                      }"
                    />
                  </div>
                </div>
              </div>

              <!-- Address Information -->
              <div class="mb-6">
                <h4 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Address</h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label for="house_no" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      House No.
                    </label>
                    <input
                      id="house_no"
                      v-model="formData.house_no"
                      type="text"
                      class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:border-transparent bg-white dark:bg-black text-gray-900 dark:text-white transition-colors"
                      :style="{
                        borderColor: brandColors.primary,
                        '--tw-ring-color': brandColors.primary,
                        'outline': 'none'
                      }"
                    />
                  </div>
                  <div>
                    <label for="street" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Street
                    </label>
                    <input
                      id="street"
                      v-model="formData.street"
                      type="text"
                      class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:border-transparent bg-white dark:bg-black text-gray-900 dark:text-white transition-colors"
                      :style="{
                        borderColor: brandColors.primary,
                        '--tw-ring-color': brandColors.primary,
                        'outline': 'none'
                      }"
                    />
                  </div>
                  <div>
                    <label for="barangay" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Barangay
                    </label>
                    <input
                      id="barangay"
                      v-model="formData.barangay"
                      type="text"
                      class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:border-transparent bg-white dark:bg-black text-gray-900 dark:text-white transition-colors"
                      :style="{
                        borderColor: brandColors.primary,
                        '--tw-ring-color': brandColors.primary,
                        'outline': 'none'
                      }"
                    />
                  </div>
                  <div>
                    <label for="city" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      City <span class="text-red-500">*</span>
                    </label>
                    <input
                      id="city"
                      v-model="formData.city"
                      type="text"
                      required
                      class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:border-transparent bg-white dark:bg-black text-gray-900 dark:text-white transition-colors"
                      :style="{
                        borderColor: brandColors.primary,
                        '--tw-ring-color': brandColors.primary,
                        'outline': 'none'
                      }"
                    />
                  </div>
                </div>
              </div>

              <!-- Footer -->
              <div class="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  @click="$emit('close')"
                  :disabled="loading"
                  class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  :disabled="loading"
                  class="px-4 py-2 text-white rounded-lg transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  :style="{ backgroundColor: brandColors.primary }"
                >
                  <svg v-if="loading" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {{ loading ? 'Saving...' : 'Save Changes' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { useBrand } from '@/composables/brand/useBrand';

interface ProfileUpdateData {
  first_name: string;
  last_name: string;
  email: string;
  mobile: string;
  house_no: string;
  street: string;
  barangay: string;
  city: string;
}

interface UserProfile {
  first_name: string;
  last_name: string;
  email: string;
  mobile?: string;
  house_no?: string;
  street?: string;
  barangay?: string;
  city?: string;
}

const props = defineProps<{
  isOpen: boolean;
  userProfile: UserProfile | null;
  loading?: boolean;
  error?: string | null;
}>();

const emit = defineEmits<{
  close: [];
  submit: [data: ProfileUpdateData];
}>();

const { brandColors } = useBrand();

const formData = ref<ProfileUpdateData>({
  first_name: '',
  last_name: '',
  email: '',
  mobile: '',
  house_no: '',
  street: '',
  barangay: '',
  city: ''
});

// Watch for user profile changes to populate form
watch(() => props.userProfile, (newProfile) => {
  if (newProfile) {
    formData.value = {
      first_name: newProfile.first_name || '',
      last_name: newProfile.last_name || '',
      email: newProfile.email || '',
      mobile: newProfile.mobile || '',
      house_no: newProfile.house_no || '',
      street: newProfile.street || '',
      barangay: newProfile.barangay || '',
      city: newProfile.city || ''
    };
  }
}, { immediate: true });

const validationError = ref<string | null>(null);

const hasValidationError = computed(() => !!validationError.value);

function handleSubmit() {
  validationError.value = null;

  if (!formData.value.mobile || !formData.value.city) {
    validationError.value = 'Mobile Number and City are required.';
    return;
  }

  emit('submit', formData.value);
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .bg-white,
.modal-leave-active .bg-white {
  transition: transform 0.3s ease;
}

.modal-enter-from .bg-white,
.modal-leave-to .bg-white {
  transform: scale(0.95);
}
</style>


